import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { AppState } from '@app/store/app.state';
import {
  GlobalSelectors,
  SiteSelectors,
  SiteActions,
  AuthSelectors,
} from '@app/store';
import { ConvertFrom24To12Pipe } from '@app/shared/pipes/convert-from-24-to-12.pipe';
import { ServiceFrequencyPipe } from '@app/shared/pipes/service-frequency.pipe';
import { Options } from 'angular2-csv';
import { CP_PERMISSIONS } from '@app/shared/app.constants';

@Component({
  selector: 'app-site-list',
  templateUrl: './site-list.component.html',
  styleUrls: ['./site-list.component.scss'],
  providers: [ServiceFrequencyPipe, ConvertFrom24To12Pipe],
})
export class SiteListComponent implements OnInit, AfterViewInit, OnDestroy {
  // handles all component subscriptions
  subscriptions: Subscription = new Subscription();

  // allActiveSites: SiteDetail[];
  allServiceSchedules: any[];
  searchText = '';
  tablePageNumber: number = null;
  csvReportDate = this.datePipe
    .transform(new Date(), 'MM/dd/yyyy')
    .replace('/', '_');

  csvReportOptions: Options = {
    filename: '',
    title: '',
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalseparator: '.',
    showLabels: false,
    headers: [
      'Site Name',
      'Address',
      'City',
      'State',
      'Zip',
      'Waste Stream',
      'Equipment',
      'Effective Date',
      'Last Scheduled',
      'Next Scheduled',
      'Is Recurring',
      'Sun',
      'Mon',
      'Tues',
      'Wed',
      'Thur',
      'Fri',
      'Sat',
    ],
    showTitle: false,
    useBom: false,
    removeNewLines: true,
    keys: [
      'accountName',
      'address1',
      'city',
      'state',
      'zip',
      'debris',
      'accountServiceEquipment',
      'effectiveDate',
      'lastScheduled',
      'nextScheduled',
      'isRecurringService',
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ],
  };

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private convertFrom24To12Pipe: ConvertFrom24To12Pipe,
    private datePipe: DatePipe
  ) { }

  ngOnInit() {
    this.subscriptions.add(
      this.store
        .select(AuthSelectors.getUserPermissions)
        .pipe(
          switchMap(userPermissions =>
            this.store.select(GlobalSelectors.getSelectedGlobalFilters).pipe(
              filter(
                selectedBillingAcc =>
                  userPermissions.includes(
                    CP_PERMISSIONS.SITES_SITEINFO_READ
                  ) && selectedBillingAcc.billingAccounts.length > 0
              ),
              distinctUntilChanged((prev, curr) => {
                return JSON.stringify(prev) === JSON.stringify(curr);
              })
            )
          )
        )
        .subscribe(selectedBillingAcc => {
          this.store.dispatch(
            SiteActions.getAllAccountServiceSchedules({
              requestBody: selectedBillingAcc,
            })
          );
        })
    );

    this.setAllServiceSchedules();
  }

  ngAfterViewInit() {
    this.checkPreviousTableState();
  }

  setAllServiceSchedules() {
    this.subscriptions.add(
      this.store
        .select(SiteSelectors.getAllActiveServiceSchedules)
        .pipe(
          filter(response => response !== null && response.length > 0),
          map(serviceSchedules =>
            serviceSchedules.map(schedule => ({
              ...schedule,
              // eslint-disable-next-line no-nested-ternary
              serviceType: schedule.isRecurringService
                ? schedule.repeat
                  ? `Recurring (${schedule.repeat})`
                  : 'Recurring'
                : 'One-Time',
              debris:
                schedule.debris === 'Unknown'
                  ? 'Unspecified Material'
                  : schedule.debris,
              effectiveDate: schedule.effectiveDate
                ? this.datePipe.transform(schedule.effectiveDate, 'MM/dd/yyyy')
                : '',
              targetIn: schedule.targetIn
                ? this.convertFrom24To12Pipe.transform(schedule.targetIn)
                : '',
              targetOut: schedule.targetOut
                ? this.convertFrom24To12Pipe.transform(schedule.targetOut)
                : '',
            }))
          )
        )
        .subscribe(allServiceSchedules => {
          this.allServiceSchedules = [];
          this.allServiceSchedules = allServiceSchedules;
        })
    );
  }

  // if user is coming from site details, get the previous table state
  checkPreviousTableState() {
    const previousTableStateSubscription = this.store
      .select(GlobalSelectors.getPreviousURL)
      .pipe(
        filter(previousURL => previousURL.includes('/organization/site/')),
        switchMap(() => this.store.select(SiteSelectors.getSiteTableState))
      )
      .subscribe(tableState => {
        this.searchText = tableState.searchText;
        this.tablePageNumber = tableState.pageNumber;
      });

    this.subscriptions.add(previousTableStateSubscription);
  }

  setSearchText(text: string) {
    this.searchText = text;
  }

  redirectToSiteDetails($event: {
    siteId: string;
    customerId: string;
    pageIndex: number;
  }) {
    // save table state when navigating to individual site
    const { customerId, siteId, pageIndex } = $event;
    const { searchText } = this;

    this.store.dispatch(
      SiteActions.setSiteListTableState({ searchText, pageNumber: pageIndex })
    );

    this.router.navigate(['organization/site/', customerId, siteId]);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
